import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HPTag from "../../common/Tag";
import type { DatasetQuestionsSearchResponse } from "@/models/Dataset";

interface DatasetQuestionRowProps {
  question: DatasetQuestionsSearchResponse;
  onSelect?: (question: DatasetQuestionsSearchResponse) => void;
  isSelected?: boolean;
  className?: string;
  compact?: boolean;
  isRecentlyUsed?: boolean;
}

const tooltipWrapperSx = {
  position: "relative" as const,
  "&:hover .custom-tooltip": {
    visibility: "visible",
    opacity: 1,
  },
};

const tooltipSx = {
  visibility: "hidden",
  opacity: 0,
  position: "absolute" as const,
  bottom: "60%",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "var(--color-hp-light-blue, #96b3e1)",
  color: "var(--color-hp-dark-blue, #5985cb)",
  border: "1px solid var(--color-hp-dark-blue, #5985cb)",
  fontSize: "0.725rem",
  fontWeight: 500,
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  letterSpacing: "0.03333em",
  lineHeight: 1.4,
  px: "8px",
  py: "4px",
  borderRadius: "4px",
  whiteSpace: "wrapAll" as const,
  maxWidth: 600,
  zIndex: 1500,
  pointerEvents: "none" as const,
  // arrow
  "&::after": {
    content: '""',
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    borderWidth: "4px",
    borderStyle: "solid",
    borderColor:
      "var(--color-hp-dark-blue, #5985cb) transparent transparent transparent",
  },
};

const RecentlyUsedBadge = () => (
  <Chip
    icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
    label="Recently used"
    size="small"
    sx={{
      height: 18,
      fontSize: "0.65rem",
      backgroundColor: "#EEF3FB",
      color: "#4A73C9",
      "& .MuiChip-icon": { color: "#4A73C9" },
      flexShrink: 0,
    }}
  />
);

export const DatasetQuestionRow: React.FC<DatasetQuestionRowProps> = ({
  question,
  onSelect,
  isSelected,
  className,
  compact = false,
  isRecentlyUsed = false,
}) => {
  const handleClick = () => {
    if (onSelect) {
      onSelect(question);
    }
  };
  if (compact) {
    return (
      <Box sx={tooltipWrapperSx}>
        <Box
          className="custom-tooltip"
          sx={{ ...tooltipSx, whiteSpace: "normal", maxWidth: 300 }}
        >
          {question.name}
        </Box>
        <Box
          onClick={handleClick}
          data-item={JSON.stringify(question)}
          className={className}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2,
            cursor: "pointer",
            color: isSelected ? "#1F68DC" : "var(--color-hp-text-day-primary)",
            backgroundColor: isSelected ? "#E3F2FD" : "transparent",
            "&:hover": {
              backgroundColor: isSelected
                ? "var(--color-hp-primary-light, #E3F2FD)"
                : "var(--color-hp-background-hover, #F5F5F5)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {question.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {question.name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <HPTag ariaLabel={`${question.type}`} color="blue">
                {question.type}
              </HPTag>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap" }}
              >
                {question.variablesCount} var
                {question.valuesCount !== undefined &&
                  ` ${question.valuesCount} val`}{" "}
                {question.responses} Responses
              </Typography>
              {isRecentlyUsed && <RecentlyUsedBadge />}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
  // Original horizontal layout for modals
  return (
    <Box sx={tooltipWrapperSx}>
      <Box className="custom-tooltip" sx={tooltipSx}>
        {question.name}
      </Box>
      <Box
        onClick={handleClick}
        data-item={JSON.stringify(question)}
        className={className}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          cursor: "pointer",
          color: isSelected ? "#1F68DC" : "var(--color-hp-text-day-primary)",
          backgroundColor: isSelected ? "#E3F2FD" : "transparent",
          "&:hover": {
            backgroundColor: isSelected
              ? "var(--color-hp-primary-light, #E3F2FD)"
              : "var(--color-hp-background-hover, #F5F5F5)",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, flex: 1, alignItems: "center" }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{ minWidth: "fit-content" }}
          >
            {question.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              maxWidth: isRecentlyUsed ? 300 : 440,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {question.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
          {isRecentlyUsed && <RecentlyUsedBadge />}
          <HPTag ariaLabel={`${question.type}`} color="blue">
            {question.type}
          </HPTag>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {question.variablesCount} var
          </Typography>
          {question.valuesCount !== undefined && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              {question.valuesCount} val
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {question.responses} Responses
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
